<!--
    see comment in _nav-layout.cshtml. Do not make any changes to the templates of
        pc-nav-layout.vue
        navHeader.vue
        navSidebar.vue
    without also making sure the corresponding changes are made to the necessary cshtml views.
-->
<template>
    <div
        v-bind:class="{ expanded: expanded }"
        class="pc-sidebar h-100 flex-shrink-0"
        data-test-role="nav-sidebar"
        v-bind:data-sidebar-expanded="expanded ? 'expanded' : null">
        <nav class="w-100 h-100 d-flex flex-column bg-primary text-white">
            <div
                v-if="showExpander"
                v-bind:class="expanderClassObj">
                <div class="m-1">
                    <header-icon
                        data-test-role="nav-sidebar-expander-button"
                        v-bind:class="{ 'rotate-180': expanded }"
                        class="m-2 pc-sidebar-menu-expander"
                        v-bind:aria-label="navSideBarLabel"
                        icon-name="chevron-double-right"
                        invert
                        v-on:click="expandedWrapper = !expandedWrapper" />
                </div>
            </div>
            <div
                v-if="showSelfDismiss"
                class="d-flex justify-content-between align-items-center pc-sidebar-dismiss-height">
                <header-icon
                    data-test-role="nav-sidebar-dismiss"
                    icon-name="list"
                    class="m-2"
                    v-bind:aria-label="navSideBarLabel"
                    invert
                    v-on:click="dismissDialog" />
            </div>
            <div class="flex-grow-1 overflow-auto">
                <nav-menu
                    v-for="menu in navigation"
                    v-bind:key="menu.MenuTypeName"
                    v-bind:ref="menu.MenuTypeName"
                    v-bind:nav-menu="menu"
                    v-bind:sidebar-expanded="expanded"
                    v-on:menu-clicked="menuClicked" />
            </div>
        </nav>
    </div>
</template>

<script>
    import navMenu from "./navMenu.vue";
    import headerIcon from "./header-icon.vue";
    import { MENU_TYPE_NAME } from "./menuTypeName";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/navSidebar.i18n";

    const EXPAND_LOCATION_TOP = false;

    // The value of expandStateKey is used in _nav-sidebar.cshtml.
    // If you change it here you must change it there too.
    const expandStateKey = "pc_expand_state_key";

    const MENU_ORDER = [
        MENU_TYPE_NAME.Home,
        MENU_TYPE_NAME.ProgramDesign,
        MENU_TYPE_NAME.ProgramAdmin,
        MENU_TYPE_NAME.Settings,
        MENU_TYPE_NAME.CPRAdmin
    ];

    export default {
        components: {
            navMenu,
            headerIcon
        },
        props: {
            initalExpandState: { type: Boolean, required: false, default: false },
            showExpander: { type: Boolean, required: false, default: true },
            showSelfDismiss: { type: Boolean, required: false, default: false }
        },
        emits: ["show-float-popper", "show-menu-popper"],
        data() {
            let expanded = this.initalExpandState;
            if (this.showExpander) {
                expanded = Boolean(JSON.parse(localStorage.getItem(expandStateKey)));
            }
            return {
                expanded
            };
        },
        computed: {
            expandedWrapper: {
                get() {
                    return this.expanded;
                },
                set(value) {
                    this.expanded = value;
                    localStorage.setItem(expandStateKey, JSON.stringify(value));
                }
            },
            expanderClassObj() {
                return {
                    "d-flex": true,
                    "border-gray-600": true,
                    "justify-content-end": true,
                    "border-bottom": EXPAND_LOCATION_TOP,
                    "border-top": !EXPAND_LOCATION_TOP,
                    "order-1": !EXPAND_LOCATION_TOP
                };
            },
            navigation() {
                const navs = [];
                MENU_ORDER.forEach((menuName) => {
                    const menu = this.$app.Navigation.find((n) => n.MenuTypeName === menuName);
                    if (menu) {
                        //need to create clone since array.sort is in place and we should not modify parent props.
                        const menuClone = JSON.parse(JSON.stringify(menu));
                        if (Array.isArray(menuClone.NavMenuItems)) {
                            menuClone.NavMenuItems.sort((itemA, itemB) => itemA.Text.localeCompare(itemB.Text));
                        }
                        navs.push(menuClone);
                    }
                });
                return navs;
            },
            ...mapI18n({ ...KEYS }, NAMESPACE)
        },
        methods: {
            dismissDialog() {
                this.$app.closeDialog();
            },
            menuClicked(menuType) {
                // this.$refs is an object where the keys are the menu type names, and the values are each 1 element arrays
                // each containing the corresponding nav menu
                // for each one, we find the navmenu and close it if it is not the currently active nav menu
                for (const menuKey of Object.keys(this.$refs)) {
                    if (this.$refs[menuKey][0].navMenu.MenuTypeName !== menuType) {
                        this.$refs[menuKey][0].close();
                    }
                }
            }
        }
    };
</script>

<style lang="scss">
    @import "./_nav-style-vars.scss";
    $transitionDuration: 0.4s;
    $expanded-width: 275px;

    .pc-sidebar {
        width: $pc-nav-program-logo-wrapper-width;
        transition-property: width;
        transition-duration: $transitionDuration;
        &.expanded {
            width: $expanded-width;
        }
    }

    .pc-sidebar-dismiss-height {
        height: $pc-nav-header-height;
    }

    .pc-sidebar-menu-expander {
        transform: rotate(0);
        transition-property: transform;
        transition-duration: $transitionDuration;
    }

    .pc-sidebar-menu-expander.rotate-180 {
        transform: rotate(180deg);
    }
</style>
